import { Badge, Group, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import { STATUS_LABEL, formatWhen, type RegistrationStatus } from "./event-queries";
import { semanticColor, type SemanticColor } from "@how/design-system";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

interface ComingUp {
  readonly registrationId: string;
  readonly title: string;
  readonly startsAt: string;
  readonly timeZone: string | null;
  readonly status: RegistrationStatus;
}

/** A person's registrations on dates ahead (#167). The policies decide what the reader sees; a child's follow ADR 0028. */
function useComingUp(personId: string) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["person-coming-up", tenantId, personId],
    enabled: tenantId !== null,
    retry: false,
    queryFn: async (): Promise<ComingUp[]> => {
      const { data, error } = await supabase
        .from("event_registrations")
        .select(
          "id, status, event_occurrences!inner(starts_at, is_cancelled, events!inner(title, campuses(time_zone)))",
        )
        .eq("tenant_id", tenantId ?? "")
        .eq("person_id", personId)
        .neq("status", "cancelled")
        .gte("event_occurrences.starts_at", new Date().toISOString())
        .limit(20);
      if (error) throw error;
      return data
        .filter((r) => !r.event_occurrences.is_cancelled)
        .map((r) => ({
          registrationId: r.id,
          title: r.event_occurrences.events.title,
          startsAt: r.event_occurrences.starts_at,
          timeZone: r.event_occurrences.events.campuses?.time_zone ?? null,
          status: r.status,
        }))
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    },
  });
}

const STATUS_COLOR: Record<RegistrationStatus, SemanticColor> = {
  registered: semanticColor("success"),
  waitlisted: semanticColor("warning"),
  cancelled: semanticColor("neutral"),
  attended: semanticColor("info"),
};

/** On the person record: what they are down for, soonest first. Nothing to change here; the event's date does that. */
export function ComingUpSection({ personId }: { personId: string }) {
  const coming = useComingUp(personId);
  if (coming.isError) return null;
  return (
    <section aria-label="Coming up">
      <Title order={4}>Coming up</Title>
      {coming.data === undefined ? null : coming.data.length === 0 ? (
        <Text size="sm" c="dimmed">
          Not registered for anything ahead.
        </Text>
      ) : (
        <Stack gap={4} mt="xs">
          {coming.data.map((r) => (
            <Group key={r.registrationId} justify="space-between" wrap="nowrap" gap="xs">
              <div>
                <Text size="sm">{r.title}</Text>
                <Text size="xs" c="dimmed">
                  {formatWhen(r.startsAt, r.timeZone)}
                </Text>
              </div>
              <Badge variant="light" color={STATUS_COLOR[r.status]}>
                {STATUS_LABEL[r.status]}
              </Badge>
            </Group>
          ))}
        </Stack>
      )}
    </section>
  );
}
